// src/modules/app-views/customers/components/EditEntityModal/index.jsx
// *******************************************************************************
// EDIT ENTITY MODAL
// *******************************************************************************
// Modal for creating or editing entities (customers, providers, companies).
// Uses GenericModal with SimpleEntityForm in hybrid mode (CPF/CNPJ auto-detect).
// Supports unique document validation with auto-load of existing entities.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { UserOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import GenericModal from 'components/core/GenericModal';
import translateX from 'utils/translateX';
import axiosService from 'services/axiosService';
import entitiesService from 'services/entitiesService';
import appService from 'services/appService';
import { API, apiUrl } from 'constants/Api';
import editEntityStep1 from './Step1';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * EditEntityModal - Modal for creating/editing entities
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.setShow - Function to set modal visibility
 * @param {Function} props.onConfirm - Callback when entity is saved (receives entity data)
 * @param {Function} props.afterClose - Callback after modal closes
 * @param {Object} props.initialData - Initial data for editing (optional, partial data allowed)
 * @param {string} props.entityId - Entity ID to fetch complete data (alternative to initialData)
 * @param {string|string[]} props.entityProfile - Entity profile(s) (customer, provider, etc.)
 */
const EditEntityModal = ({
  show,
  setShow,
  onConfirm,
  afterClose,
  initialData = null,
  entityId = null,
  entityProfile = 'customer',
  requiredFields = [],
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Determine the entity ID from props (entityId takes precedence, then initialData._id)
  const targetEntityId = entityId || initialData?._id;
  const isEditing = !!targetEntityId;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Fetched entity data (complete data from API)
  const [fetchedEntity, setFetchedEntity] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Original data snapshot for change detection (to avoid unnecessary API calls)
  const originalDataRef = useRef(null);

  // Use fetched data if available, otherwise fall back to initialData
  const entityData = fetchedEntity || initialData;

  const [entityState, setEntityState] = useState({
    entityType: entityData?.entityType || 'company',
    countryCode: entityData?.countryCode || 'BR',
  });

  // Track unique entity found during document search
  const [uniqueEntity, setUniqueEntity] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Fetch complete entity data when modal opens with entityId
  useEffect(() => {
    if (!show || !targetEntityId) {
      return;
    }

    // If we already have complete data (from previous fetch or full initialData), skip
    if (fetchedEntity?._id === targetEntityId) {
      return;
    }

    const fetchEntityData = async () => {
      setIsLoading(true);
      setFetchError(null);

      try {
        const url = apiUrl.build({
          url: API.ENTITIES.URL,
          params: { _id: targetEntityId },
        });

        const [result] = await axiosService.get({
          url,
          returnAsObject: true,
        });

        if (result) {
          setFetchedEntity(result);
          // Save original data snapshot for change detection
          originalDataRef.current = JSON.parse(JSON.stringify(result));
          // Update entityState with fetched data
          setEntityState({
            entityType: result.entityType || 'company',
            countryCode: result.countryCode || 'BR',
          });
        } else {
          setFetchError('entity_not_found');
        }
      } catch (error) {
        console.error('[EditEntityModal:fetchEntityData] Error:', error);
        setFetchError(error?.message || 'error_loading_entity');
        appService.notification({
          flag: 'e',
          title: translateX('error'),
          description: translateX('error_loading_entity'),
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntityData();
  }, [show, targetEntityId, fetchedEntity?._id]);

  // Save/update original data when uniqueEntity is found (CNPJ blur found existing entity in DB)
  // This handles the case where user changes CNPJ and finds a DIFFERENT entity
  useEffect(() => {
    if (!uniqueEntity?._id) return;

    // Check if uniqueEntity is different from the originally loaded entity
    const originalEntityId = fetchedEntity?._id || targetEntityId;
    const isDifferentEntity = uniqueEntity._id !== originalEntityId;

    console.log('[EditEntityModal:useEffect:uniqueEntity]', {
      uniqueEntityId: uniqueEntity._id,
      originalEntityId,
      isDifferentEntity,
      currentOriginalDataRef: originalDataRef.current?._id,
    });

    // Always update originalDataRef when uniqueEntity changes
    // This ensures we track the "current" entity for change detection
    if (isDifferentEntity || !originalDataRef.current) {
      console.log(
        '[EditEntityModal:useEffect:uniqueEntity] Updating originalDataRef to uniqueEntity'
      );
      originalDataRef.current = JSON.parse(JSON.stringify(uniqueEntity));
    }
  }, [uniqueEntity, fetchedEntity?._id, targetEntityId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = useCallback(
    async ({ form }) => {
      const values = form.getFieldsValue(true);

      // Determine the correct entity ID to use:
      // 1. If uniqueEntity exists with _id (found in DB via CNPJ search) → use that
      // 2. If uniqueEntity exists without _id (from API, new entity) → create new (no ID)
      // 3. If no uniqueEntity → use original entityData._id
      const originalEntityId = fetchedEntity?._id || targetEntityId;
      const uniqueEntityId = uniqueEntity?._id;

      // Check if user switched to a different entity via CNPJ search
      const switchedToNewEntity = uniqueEntity && !uniqueEntity._id; // From API, not in DB yet
      const switchedToDifferentEntity = uniqueEntityId && uniqueEntityId !== originalEntityId;

      // Determine final entity ID:
      // - If switched to new entity from API → null (will POST)
      // - If switched to different entity in DB → use that entity's ID
      // - Otherwise → use original entity ID (or null for new)
      let finalEntityId;
      if (switchedToNewEntity) {
        finalEntityId = null; // Force POST for new entity from API
      } else if (switchedToDifferentEntity) {
        finalEntityId = uniqueEntityId; // Use the different entity's ID
      } else {
        finalEntityId = uniqueEntityId || values.entityId || entityData?._id;
      }

      const isUpdate = !!finalEntityId;

      console.log('[EditEntityModal:handleFinish] Context:', {
        originalEntityId,
        uniqueEntityId,
        switchedToNewEntity,
        switchedToDifferentEntity,
        finalEntityId,
        isUpdate,
        originalDataRefId: originalDataRef.current?._id,
      });

      // Check if we're editing an existing entity and if there are changes
      // Compare with originalDataRef which is updated when uniqueEntity changes
      if (isUpdate && originalDataRef.current) {
        const hasChanges = hasFormChanges(values, originalDataRef.current);

        console.log('[EditEntityModal:handleFinish] Change detection:', {
          hasChanges,
          formValuesName: values.name,
          originalDataName: originalDataRef.current?.name,
        });

        if (!hasChanges) {
          // No changes detected - just close the modal without API request
          // Return the current entity data (uniqueEntity if switched, or original)
          const entityToReturn = switchedToDifferentEntity ? uniqueEntity : originalDataRef.current;
          console.log(
            '[EditEntityModal:handleFinish] No changes, returning entity:',
            entityToReturn?._id
          );
          onConfirm?.(entityToReturn);
          return true; // Close modal
        }
      }

      const msg = await appService.message({
        flag: 'i',
        text: isUpdate ? 'updating' : 'saving',
        key: 'saveEntity',
        duration: 0,
      });

      try {
        // Prepare data using decorator
        // Use uniqueEntity as base if switched to different entity
        const baseData = switchedToDifferentEntity ? uniqueEntity : entityData || {};
        const decoratedData = entitiesService.decorator(values, baseData, {});

        // Normalize entityProfile - ensure it's always an array for backend
        const profileValue = values.entityProfile || entityProfile;
        const normalizedProfile = Array.isArray(profileValue) ? profileValue : [profileValue];

        // Build request body - IMPORTANT: Remove _id to avoid MongoDB error
        const { _id: removedId, ...bodyWithoutId } = decoratedData;
        const body = {
          ...bodyWithoutId,
          entityProfile: normalizedProfile,
          entityType: entityState.entityType || values.entityType,
        };

        // For new entities (manual registration), explicitly set origin to 0 (manual)
        // This prevents backend from defaulting to origin: 2 (automatic)
        // Even if data was enriched via API (CNPJ lookup), the registration is manual
        if (!isUpdate) {
          body._metadata = { ...body._metadata, origin: 0 };
        }

        console.log('[EditEntityModal:handleFinish] Saving:', {
          finalEntityId,
          isUpdate,
          removedId,
          bodyKeys: Object.keys(body),
        });

        let savedEntity;

        if (isUpdate) {
          // Update existing entity
          const url = apiUrl.build({
            url: API.ENTITIES.URL,
            params: { _id: finalEntityId },
          });

          const [result] = await axiosService.put({
            url,
            data: body,
            headers: { 'x-resultasobject': true },
          });

          savedEntity = result;
        } else {
          // Create new entity
          const url = API.ENTITIES.URL.replace(':_id?', '');

          const [result] = await axiosService.post({
            url,
            data: body,
            headers: { 'x-resultasobject': true },
          });

          savedEntity = result;
        }

        if (savedEntity) {
          msg.update({
            flag: 's',
            text: isUpdate ? 'document_updated_with_success' : 'document_saved_with_success',
          });

          // Call onConfirm with the saved entity
          onConfirm?.(savedEntity);
          return true; // Close modal
        }

        return false; // Keep modal open on error
      } catch (error) {
        console.error('[EditEntityModal:handleFinish] Error:', error);

        msg.update({
          flag: 'e',
          text: isUpdate ? 'error_updating_document' : 'error_saving_document',
        });

        appService.notification({
          flag: 'e',
          title: translateX('error'),
          description: error?.message || translateX('error_saving_entity'),
        });

        return false; // Keep modal open on error
      }
    },
    [
      entityData,
      entityProfile,
      entityState.entityType,
      uniqueEntity,
      onConfirm,
      fetchedEntity?._id,
      targetEntityId,
    ]
  );

  const handleAfterClose = useCallback(() => {
    setEntityState({ entityType: 'company', countryCode: 'BR' });
    setUniqueEntity(null);
    setFetchedEntity(null);
    setFetchError(null);
    setIsLoading(false);
    originalDataRef.current = null; // Reset original data for next open
    afterClose?.();
  }, [afterClose]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => {
    // Ensure entityProfile is always an array for mode="multiple"
    const normalizeProfile = profile => {
      if (!profile) return ['customer'];
      return Array.isArray(profile) ? profile : [profile];
    };

    if (entityData) {
      // Sanitize data to remove 'key' properties (reserved React prop)
      const sanitizedData = sanitizeFormData(entityData);
      return {
        ...sanitizedData,
        entityProfile: normalizeProfile(entityData.entityProfile || entityProfile),
      };
    }

    return {
      entityType: 'company',
      entityProfile: normalizeProfile(entityProfile),
      countryCode: 'BR',
    };
  }, [entityData, entityProfile]);

  // Normalize entityProfile to array for defaultEntityProfiles
  const defaultEntityProfiles = useMemo(() => {
    const profile = entityData?.entityProfile || entityProfile;
    return Array.isArray(profile) ? profile : [profile];
  }, [entityData?.entityProfile, entityProfile]);

  // Build steps with loading state handling
  const steps = useMemo(() => {
    // If loading, show a loading spinner instead of the form
    if (isLoading) {
      return [
        {
          stepTitle: 'loading',
          content: () => (
            <Spin size="large" tip={translateX('loading_entity_data')}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 300,
                }}
              />
            </Spin>
          ),
        },
      ];
    }

    // If there was an error fetching, show error message
    if (fetchError) {
      return [
        {
          stepTitle: 'error',
          content: () => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 300,
                color: '#ff4d4f',
              }}
            >
              {translateX(fetchError)}
            </div>
          ),
        },
      ];
    }

    // Normal form step - pass entityData for form population
    return [editEntityStep1({ entityState, setEntityState, defaultEntityProfiles, entityData })];
  }, [entityState, defaultEntityProfiles, isLoading, fetchError, entityData]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={isEditing ? 'edit_customer' : 'new_customer'}
      icon={<UserOutlined />}
      steps={steps}
      onFinish={handleFinish}
      initialValues={initialValues}
      width={800}
      maskClosable={false}
      afterClose={handleAfterClose}
      requiredFields={requiredFields}
      confirmDisabled={isLoading || !!fetchError}
      formProps={{
        setUniqueEntity,
        reloadEntirePage: false,
        requiredFields,
        // Pass entityState for resolveRequiredFields to use when form values aren't set yet
        entityState,
        // Pass entityData for components to check dataEnrichment status
        // This enables locking CNPJ/CPF/foreignIdentifier fields when entity was enriched via API
        data: entityData,
      }}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default EditEntityModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Recursively remove 'key' properties from an object.
 * This is necessary because 'key' is a reserved React prop and causes warnings
 * when passed to Ant Design Form's initialValues.
 */
function sanitizeFormData(obj) {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(sanitizeFormData);
  if (typeof obj !== 'object') return obj;
  if (obj instanceof Date) return obj;

  const result = {};
  for (const [k, v] of Object.entries(obj)) {
    // Skip 'key' property (reserved React prop)
    if (k === 'key') continue;
    result[k] = sanitizeFormData(v);
  }
  return result;
}

/**
 * Compare form values with original data to detect changes.
 * Only compares relevant editable fields to avoid false positives.
 * @param {Object} formValues - Current form values
 * @param {Object} originalData - Original data snapshot
 * @returns {boolean} True if there are changes, false otherwise
 */
function hasFormChanges(formValues, originalData) {
  if (!originalData) return true; // No original data, assume changes

  // Fields to compare (editable fields in the entity form)
  const fieldsToCompare = [
    'name',
    'fullName',
    'razaoSocial',
    'entityType',
    'entityProfile',
    'countryCode',
    // Documents - compare nested value
    'cnpj.value',
    'cpf.value',
    'ie.value',
    'foreignIdentifier',
    // Contact info
    'email.value',
    'phone.value',
    // Address fields
    'address.zip',
    'address.address1',
    'address.number',
    'address.address2',
    'address.neighborhood',
    'address.city',
    'address.cityCode',
    'address.region',
    'address.country',
  ];

  for (const fieldPath of fieldsToCompare) {
    const formValue = getNestedValue(formValues, fieldPath);
    const originalValue = getNestedValue(originalData, fieldPath);

    // Normalize values for comparison
    const normalizedForm = normalizeValue(formValue);
    const normalizedOriginal = normalizeValue(originalValue);

    // Compare arrays specially (for entityProfile)
    if (Array.isArray(normalizedForm) || Array.isArray(normalizedOriginal)) {
      const formArray = Array.isArray(normalizedForm)
        ? normalizedForm.sort()
        : [normalizedForm].filter(Boolean);
      const originalArray = Array.isArray(normalizedOriginal)
        ? normalizedOriginal.sort()
        : [normalizedOriginal].filter(Boolean);

      if (formArray.length !== originalArray.length) return true;
      if (formArray.some((v, i) => v !== originalArray[i])) return true;
      continue;
    }

    if (normalizedForm !== normalizedOriginal) {
      return true; // Found a change
    }
  }

  return false; // No changes detected
}

/**
 * Get nested value from object using dot notation path
 */
function getNestedValue(obj, path) {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

/**
 * Normalize value for comparison (handle empty strings, null, undefined)
 */
function normalizeValue(value) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') return value.trim();
  return value;
}
